import { decodeWorkspaceSecretV2 } from '@secureblob/sdk/workspaceSecretV2'
import type { ComponentJobServerRunner } from '@secureblob/sdk/componentJobs'
import type { EngineManager, WorkspaceEntry } from '../../engine/types'
import { buildNewConnection } from '../../engine/mcp/client'
import { createConfigBlob, repushConfigBlobIfMissing } from '../../engine/mcp/configBlobService'
import type { McpConfig, McpConfigOwner, McpConnectionEntry as FullMcpConnectionEntry } from '../../engine/mcp/types'
import { buildCreatePayload, buildEditPayload, requiresConnectionRecreation } from '../../engine/mcp/connectionEditing'
import { grantMcpWorkspaceAccess, revokeMcpWorkspaceAccess } from '../../engine/mcp/accessControl'
import { getConfigReaderSeed } from '../../engine/mcp/crypto'

type ReusableMcpConnectionEntry = FullMcpConnectionEntry

interface EnsureComponentJobServerRunnerOptions {
    engine: EngineManager
    workspaceEntry: WorkspaceEntry
    sourceName: string
    existingServerRunner?: ComponentJobServerRunner | null
}

export async function ensureComponentJobServerRunner(
    options: EnsureComponentJobServerRunnerOptions,
): Promise<ComponentJobServerRunner> {
    const { engine, workspaceEntry, sourceName, existingServerRunner } = options
    const decodedSecret = decodeWorkspaceSecretV2(workspaceEntry.capability)
    if (!decodedSecret) {
        throw new Error('Component jobs server-runner setup requires a valid workspace-secret-v2 for the active workspace.')
    }
    if (!decodedSecret.writerSeed) {
        throw new Error(`Workspace '${workspaceEntry.label}' requires writer/manage access to issue a component job server-runner credential.`)
    }

    const apiBaseUrl = workspaceEntry.apiBaseUrl.trim().replace(/\/+$/, '')
    const canWrite = !!decodedSecret.writerSeed && workspaceEntry.canWrite !== false
    const label = buildServerRunnerLabel(sourceName)
    const registryEntries = engine.getRegistry().map(entry => ({
        blobId: entry.blobId,
        label: entry.label,
        emoji: entry.emoji,
        capability: entry.capability,
    }))
    const existingConnection = findReusableConnection(engine, workspaceEntry, existingServerRunner)

    if (existingServerRunner && existingConnection) {
        const owner = buildConnectionOwner(engine, workspaceEntry, existingConnection.ownerKid)
        const { configs } = buildEditPayload(
            new Set([workspaceEntry.blobId]),
            existingConnection.mcpWriterSeeds?.[workspaceEntry.blobId] ? new Set([workspaceEntry.blobId]) : new Set<string>(),
            registryEntries,
            existingConnection.mcpReaderSeeds ?? {},
            existingConnection.mcpWriterSeeds ?? {},
        )
        if (configs.length > 0) {
            const config: McpConfig = {
                v: 1,
                type: 'secureblob-mcp-config',
                label: existingConnection.label,
                owner,
                workspaces: configs,
            }
            await repushConfigBlobIfMissing(
                apiBaseUrl,
                existingConnection.configBlobId,
                existingConnection.readKey,
                getConfigReaderSeed(existingConnection),
                existingConnection.writerSeed,
                config,
            )
            return existingServerRunner
        }
    }

    const owner = buildConnectionOwner(engine, workspaceEntry)
    const { configs, mcpReaderSeeds, mcpWriterSeeds } = buildCreatePayload(
        new Set([workspaceEntry.blobId]),
        canWrite ? new Set([workspaceEntry.blobId]) : new Set<string>(),
        registryEntries,
    )
    const workspaceConfig = configs[0]
    if (!workspaceConfig) {
        throw new Error(`Workspace '${workspaceEntry.label}' could not be prepared for component job server-runner access.`)
    }

    const { entry, token, config } = buildNewConnection(
        label,
        [workspaceConfig],
        owner,
        mcpReaderSeeds,
        mcpWriterSeeds,
        apiBaseUrl,
        {
            purpose: 'server-runner',
            linkedWorkspaceRefs: [workspaceEntry.blobId],
        },
    )

    const grantedReaderSeed = mcpReaderSeeds[workspaceEntry.blobId]
    const grantedWriterSeed = mcpWriterSeeds[workspaceEntry.blobId]
    let grantedAccess = false
    try {
        if (grantedReaderSeed) {
            await grantMcpWorkspaceAccess(
                apiBaseUrl,
                decodedSecret,
                grantedReaderSeed,
                grantedWriterSeed,
            )
            grantedAccess = true
        }

        await createConfigBlob(
            apiBaseUrl,
            entry.configBlobId,
            entry.readKey,
            entry.readerSeed!,
            entry.writerSeed,
            config,
        )

        engine.addMcpConnection(entry)
    } catch (error) {
        if (grantedAccess) {
            try {
                await revokeMcpWorkspaceAccess(
                    apiBaseUrl,
                    decodedSecret,
                    grantedReaderSeed,
                    grantedWriterSeed,
                )
            } catch {
                // Best-effort rollback only.
            }
        }
        throw error
    }

    return {
        purpose: 'server-runner',
        label,
        configBlobId: entry.configBlobId,
        connectionCredential: token,
    }
}

function buildConnectionOwner(
    engine: EngineManager,
    workspaceEntry: WorkspaceEntry,
    preferredKid?: string,
): McpConfigOwner {
    if (preferredKid) {
        const profile = engine.lookupKid(preferredKid)
        if (profile) {
            return {
                primaryKid: preferredKid,
                displayName: profile.name ?? preferredKid.slice(0, 8),
                ...(profile.emoji ? { emoji: profile.emoji } : {}),
            }
        }
    }

    const identity = engine.getIdentity()
    if (identity) {
        return {
            primaryKid: identity.kid,
            displayName: identity.name,
            ...(identity.emoji ? { emoji: identity.emoji } : {}),
        }
    }

    return {
        primaryKid: `component-job-server-runner:${workspaceEntry.blobId}`,
        displayName: 'SecureBlob Component Jobs',
    }
}

function buildServerRunnerLabel(sourceName: string): string {
    const trimmed = sourceName.trim()
    return trimmed.length > 0 ? `Server runner: ${trimmed}` : 'Server runner'
}

function findReusableConnection(
    engine: EngineManager,
    workspaceEntry: WorkspaceEntry,
    existingServerRunner?: ComponentJobServerRunner | null,
): ReusableMcpConnectionEntry | null {
    if (
        !existingServerRunner
        || existingServerRunner.configBlobId.trim().length === 0
        || existingServerRunner.connectionCredential.trim().length === 0
    ) {
        return null
    }

    const existingConnection = engine.getMcpConnections().find(connection =>
        connection.configBlobId === existingServerRunner.configBlobId,
    ) as ReusableMcpConnectionEntry | undefined
    if (!existingConnection) return null
    if (requiresConnectionRecreation(existingConnection)) return null
    if (existingConnection.workspaceBlobIds.length !== 1) return null
    if (existingConnection.workspaceBlobIds[0] !== workspaceEntry.blobId) return null
    if (!existingConnection.mcpReaderSeeds?.[workspaceEntry.blobId]) return null
    return existingConnection
}
